import { CallStatus } from "@prisma/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { getCallAnalysisWorkspace } from "@/app/actions/call/workspace/action";
import { buildCallsQueryKey } from "@/app/hooks/fetchs/calls/useFetchCalls";
import { CallAnalysisWorkspace } from "@/app/types/call/CallAnalysisWorkspace";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { QueryKey } from "@/app/vars/QueryKeys";

type Options = {
  projectId?: number;
  offerId?: number;
  page: number;
  limit: number;
  status?: CallStatus | null;
  filter?: CallScopeFilter;
  enabled?: boolean;
  initialWorkspace?: CallAnalysisWorkspace;
};

// One batched round-trip that seeds the calls / members / call-types caches at once,
// so the page's initial load isn't a serialized waterfall of separate Server Actions.
// Keyed on projectId only (one-shot per project): page / filter changes go through the
// individual hooks, which read from the seeded caches and stay the invalidation targets.
export function useFetchCallAnalysisWorkspace(options: Options) {
  const {
    projectId,
    offerId,
    page,
    limit,
    status,
    filter = {},
    enabled = true,
    initialWorkspace,
  } = options;
  const queryClient = useQueryClient();

  const seedCaches = (workspace: CallAnalysisWorkspace) => {
    queryClient.setQueryData(
      buildCallsQueryKey(projectId, offerId, page, limit, status, filter),
      workspace.calls,
    );
    queryClient.setQueryData([QueryKey.PROJECT_MEMBERS, projectId], workspace.members);
    queryClient.setQueryData([QueryKey.PROJECT_CALL_TYPES, projectId], workspace.callTypes);
  };

  const scope = projectId ? `${projectId}:${offerId ?? ""}` : undefined;
  const seededScope = useRef<string | undefined>(undefined);
  if (initialWorkspace && scope && seededScope.current !== scope) {
    seedCaches(initialWorkspace);
    seededScope.current = scope;
  }

  return useQuery({
    queryKey: [QueryKey.CALL_ANALYSIS_WORKSPACE, projectId, offerId ?? null],
    queryFn: async () => {
      const workspace = await getCallAnalysisWorkspace(
        projectId!,
        offerId,
        page,
        limit,
        status,
        filter,
      );
      seedCaches(workspace);
      return workspace;
    },
    enabled: enabled && !!projectId,
    staleTime: Infinity,
    initialData: seededScope.current === scope ? initialWorkspace : undefined,
  });
}
